import React, { useState } from "react";
import styled from "styled-components";
import Spinner from "react-bootstrap/Spinner";
import { Modal, StyledModal } from "ui/Modal";
import { BasicButton } from "ui/Buttons";
import { getCustomersPage } from "services/customer.services";
import {
  getReminderRecommendation,
  getmanagesettingdaata,
  managesettingdataadd,
} from "services/supplier.services";
import EmailTemplateService from "services/emailTemplate.services";
import { useGetAllEmailTemplates } from "queries/emailTemplates";

const PAGE_SIZE = 100;
// Each customer costs up to four requests, so they are run a few at a time:
// serial is too slow across a whole book, unbounded floods the API.
const CONCURRENCY = 4;

const Field = styled.div`
  margin-bottom: 16px;
  label {
    display: block;
    margin-bottom: 6px;
    font-family: Montserrat Alternates-semiBold;
    font-size: 11px;
    color: #808186;
  }
  select {
    width: 100%;
    height: 32px;
    padding: 0 8px;
    background: #3a3b48;
    border: 1px solid #707070;
    border-radius: 10px;
    font-family: Montserrat Alternates;
    font-size: 12px;
    color: #c4c4c4;
  }
`;

const Note = styled.div`
  font-family: Montserrat Alternates;
  font-size: 11px;
  line-height: 17px;
  color: #808186;
  margin-bottom: 16px;
`;

const Progress = styled.div`
  font-family: Montserrat Alternates;
  font-size: 12px;
  color: #c4c4c4;
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 40px;
`;

const Result = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 16px 0;
  li {
    font-family: Montserrat Alternates;
    font-size: 12px;
    line-height: 22px;
    color: #c4c4c4;
    display: flex;
    justify-content: space-between;
  }
  li span:last-child {
    font-family: Montserrat Alternates-semiBold;
    color: #fff;
  }
`;

// Runs `worker` over `items` a few at a time.
const runPooled = async (items, limit, worker) => {
  let cursor = 0;
  const runners = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (cursor < items.length) {
      const index = cursor;
      cursor += 1;
      await worker(items[index]);
    }
  });
  await Promise.all(runners);
};

// Every customer in the tenant, not just the page on screen.
//
// /customers/manage-notifications responds { length, customers, customerTypes }
// and pages from 0 (offset = page * limit) — see newCustomerList in the API.
// The listing's own hook reads a different endpoint with a different shape, so
// it can't be used as the reference here.
const fetchAllCustomers = async () => {
  const all = [];
  let page = 0;
  for (;;) {
    const res = await getCustomersPage(page, PAGE_SIZE);
    const batch = res?.customers || [];
    all.push(...batch);
    const total = Number(res?.length) || 0;
    // A short page is the end regardless of what `length` says, so a missing
    // total can never turn this into an endless loop.
    if (batch.length < PAGE_SIZE || (total && all.length >= total)) break;
    page += 1;
  }
  return all;
};

// Mirrors the single-customer Apply in CustomerReminder: same field names, same
// fallbacks. Notably the recommendation writes weekDays and the settings record
// expects weekdays, and fields the recommendation omits (parent-only, and the
// escalation pair on low/medium tiers) keep whatever was already saved rather
// than being blanked.
const buildSettingsBody = (customerId, existing, recommendation) => {
  const ms = recommendation.manageSettings || {};
  const current = existing?.manageSettings || {};
  const escalateOn = recommendation.daysToEscalate !== undefined;
  const emailLimitOn = recommendation.escalationEmailLimit !== undefined;

  return {
    _id: existing?._id ? existing._id : undefined,
    customer_id: customerId,
    counterPartyId: customerId,
    manageSettings: {
      onDueDate: !!ms.onDueDate,
      beforeDueDate: {
        isBeforeDueDate: !!ms.beforeDueDate?.isBeforeDueDate,
        days: +(ms.beforeDueDate?.days ?? 1),
      },
      afterDueDate: {
        isAfterDueDate: !!ms.afterDueDate?.isAfterDueDate,
        days: +(ms.afterDueDate?.days ?? 1),
      },
      // Not part of any recommendation; carried over from the saved record so
      // the whole-subdocument $set doesn't clear it.
      afterInvoiceCreation: {
        isAfterInvoiceCreation: !!current.afterInvoiceCreation?.isAfterInvoiceCreation,
        days: +(current.afterInvoiceCreation?.days ?? 1),
      },
      customerLevel: {
        isOnSelectedDate: !!ms.customerLevel?.isOnSelectedDate,
        isOnSelectedDayOfWeek: !!ms.customerLevel?.isOnSelectedDayOfWeek,
        weekdays: ms.customerLevel?.weekDays
          ? ms.customerLevel.weekDays.toLowerCase()
          : current.customerLevel?.weekdays || "monday",
        monthDate: +(ms.customerLevel?.monthDate ?? current.customerLevel?.monthDate ?? 1),
      },
      isParentOnlyEnable: current.isParentOnlyEnable ?? false,
    },
    isDaysToEscalate: escalateOn,
    daysToEscalate: escalateOn ? recommendation.daysToEscalate : existing?.daysToEscalate,
    escalationEmailLimit: emailLimitOn
      ? recommendation.escalationEmailLimit
      : existing?.escalationEmailLimit,
    isEscalationEmailLimit: emailLimitOn,
  };
};

export default function ApplyAllRecommendations({ onDone }) {
  const [open, setOpen] = useState(false);
  const [templateId, setTemplateId] = useState("");
  const [phase, setPhase] = useState("idle"); // idle | running | done
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [summary, setSummary] = useState(null);

  const { data: templateData, isLoading: templatesLoading } = useGetAllEmailTemplates();
  // getAllEmailTemplates resolves to the array itself (see how
  // CustomerNotification maps straight over it), not a wrapped response.
  const templates = Array.isArray(templateData) ? templateData : templateData?.data || [];

  const close = () => {
    if (phase === "running") return;
    setOpen(false);
    setPhase("idle");
    setSummary(null);
    setProgress({ done: 0, total: 0 });
  };

  const run = async () => {
    setPhase("running");
    setSummary(null);

    const counts = {
      applied: 0,
      noRecommendation: 0,
      templatesAssigned: 0,
      failed: 0,
    };

    let customers = [];
    try {
      customers = await fetchAllCustomers();
    } catch (err) {
      console.error("could not load the customer list", err);
      setPhase("done");
      setSummary({ ...counts, loadFailed: true, total: 0 });
      return;
    }

    setProgress({ done: 0, total: customers.length });

    await runPooled(customers, CONCURRENCY, async (customer) => {
      const id = customer?.Id;
      try {
        if (!id) return;

        // Customers with a template keep it; only the ones without get the
        // template chosen for this run. This has to happen before the settings
        // save: the save is an update with no upsert, so a customer with no
        // preferences row yet would be silently skipped — assigning the template
        // is what creates that row.
        if (!customer?.preferences?.emailTemplateId && templateId) {
          await EmailTemplateService.postAssignATemplateToCustomer({
            Id: id,
            _id: templateId,
          });
          counts.templatesAssigned += 1;
        }

        const recRes = await getReminderRecommendation(id);
        const recommendation = recRes?.data?.data?.[0]?.recommendation;
        if (!recommendation) {
          counts.noRecommendation += 1;
          return;
        }

        // The save does $set on the whole manageSettings subdocument, so any
        // field left out of the body is wiped, not kept. The current record is
        // read so the fields a recommendation never carries survive the write.
        let existing = null;
        try {
          const settingsRes = await getmanagesettingdaata({ customer_id: id });
          existing = settingsRes?.data?.data || null;
        } catch (err) {
          existing = null;
        }

        await managesettingdataadd(buildSettingsBody(id, existing, recommendation));
        counts.applied += 1;
      } catch (err) {
        console.error("failed to apply the recommendation for", id, err);
        counts.failed += 1;
      } finally {
        setProgress((prev) => ({ ...prev, done: prev.done + 1 }));
      }
    });

    setSummary({ ...counts, total: customers.length });
    setPhase("done");
    if (onDone) onDone();
  };

  return (
    <>
      <BasicButton
        type="button"
        width="130px"
        style={{ whiteSpace: "nowrap" }}
        onClick={() => setOpen(true)}
      >
        Set AI Default
      </BasicButton>

      {open && (
        <Modal show={open} onHide={close} centered style={{ zIndex: "100000" }}>
          {/* Bootstrap already wraps children in its own .modal-content at the
              dialog's width; a fixed narrower width here left a strip of that
              outer box showing down the right edge. Fill it instead. */}
          <div className="modal-content" style={{ width: "100%", padding: "19px" }}>
            <StyledModal>
              <Modal.Header
                closeButton={phase !== "running"}
                style={{
                  padding: 0,
                  margin: 0,
                  borderBottom: "1px solid #707070",
                  paddingBottom: "15px",
                }}
              >
                <div className="upper-section">
                  <h1>Set AI Default</h1>
                </div>
              </Modal.Header>

              <Modal.Body style={{ padding: 0, paddingTop: "16px" }}>
                {phase === "done" && summary ? (
                  <>
                    {summary.loadFailed ? (
                      <Note>Could not load the customer list. Nothing was changed.</Note>
                    ) : (
                      <Result>
                        <li>
                          <span>Customers processed</span>
                          <span>{summary.total}</span>
                        </li>
                        <li>
                          <span>Settings applied</span>
                          <span>{summary.applied}</span>
                        </li>
                        <li>
                          <span>Email templates assigned</span>
                          <span>{summary.templatesAssigned}</span>
                        </li>
                        <li>
                          <span>No recommendation yet</span>
                          <span>{summary.noRecommendation}</span>
                        </li>
                        <li>
                          <span>Failed</span>
                          <span>{summary.failed}</span>
                        </li>
                      </Result>
                    )}
                    <BasicButton type="button" width="100%" onClick={close}>
                      Close
                    </BasicButton>
                  </>
                ) : phase === "running" ? (
                  <Progress>
                    <Spinner animation="border" variant="secondary" size="sm" />
                    {progress.total
                      ? `Applying… ${progress.done} of ${progress.total} customers`
                      : "Loading customers…"}
                  </Progress>
                ) : (
                  <>
                    <Note>
                      Every customer with a recommendation will have their reminder
                      settings replaced by it. Customers who already have an email
                      template keep it; the template chosen here is given only to those
                      without one.
                    </Note>
                    <Field>
                      <label htmlFor="bulkTemplate">Email template</label>
                      <select
                        id="bulkTemplate"
                        value={templateId}
                        onChange={(e) => setTemplateId(e.target.value)}
                      >
                        <option value="">
                          {templatesLoading ? "Loading templates…" : "Select a template"}
                        </option>
                        {templates.map((template) => (
                          <option key={template._id} value={template._id}>
                            {template.emailTemplateName || template.name || template._id}
                          </option>
                        ))}
                      </select>
                    </Field>
                    <BasicButton
                      type="button"
                      width="100%"
                      onClick={run}
                      disabled={!templateId}
                    >
                      Apply to all customers
                    </BasicButton>
                  </>
                )}
              </Modal.Body>
            </StyledModal>
          </div>
        </Modal>
      )}
    </>
  );
}
